import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import DownloadIcon from '@mui/icons-material/Download';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  IconButton,
  Link,
  Snackbar,
  Typography,
  useTheme,
} from '@mui/material';
import { Icon } from '@iconify/react';
import {
  ArrowBack,
  Description,
  ExpandMore,
  Inventory,
  Launch,
  Link as LinkIcon,
} from '@mui/icons-material';

import {
  isInitializingAtom,
  locationsAtom,
  refinersAtom,
  selectedLocationIdAtom,
  selectedSiteAtom,
  selectedStateAtom,
  sitesAtom,
} from '../utils/AtomConfig';
import Constants from '../utils/Constants';
import { ExternalLink } from '../models/ExternalLink';
import { ISite } from '../models/ISite';
import { QuickReference } from '../models/QuickReference';
import { SharePointService } from '../utils/SharePointService';
import { LoadingSpinner } from './LoadingSpinner';
import FilePreview from './FilePreview';
import { IFile } from '../models/IFile';
import { RecentDocumentsCard } from './RecentDocumentsCard';

export function SiteSelector() {
  const theme = useTheme();

  const [selectedState, setSelectedState] = useAtom(selectedStateAtom);
  const [sites, setSites] = useAtom(sitesAtom);
  const allLocations = useAtomValue(locationsAtom);
  const setSelectedSite = useSetAtom(selectedSiteAtom);
  const setSelectedLocationId = useSetAtom(selectedLocationIdAtom);
  const setRefiners = useSetAtom(refinersAtom);
  const setIsInitializing = useSetAtom(isInitializingAtom);

  const [isShowWaitMessage, setIsShowWaitMessage] = React.useState(false);

  const history = useHistory();
  const sharepointService = SharePointService.Instance;

  const [selectedFile, setSelectedFile] = React.useState<
    QuickReference | IFile | null
  >(null);

  const sitesQuery = useQuery<ISite[]>(
    Constants.QUERY_KEYS.SITES,
    () => sharepointService.getSites(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    }
  );

  useEffect(() => {
    if (sitesQuery.data) {
      setSites(sitesQuery.data);
    }
  }, [sitesQuery.data, setSites]);

  const externalLinksQuery = useQuery<ExternalLink[]>(
    Constants.QUERY_KEYS.EXTERNAL_LINKS,
    () => sharepointService.getExternalLinks(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    }
  );

  const quickReferencesQuery = useQuery<QuickReference[]>(
    Constants.QUERY_KEYS.QUICK_REFERENCES,
    () => sharepointService.getQuickReferences(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    }
  );

  const refinersQuery = useQuery(
    Constants.QUERY_KEYS.REFINERS,
    () => sharepointService.getRefiners(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    }
  );

  const recentDocumentsQuery = useQuery(
    [Constants.QUERY_KEYS.RECENT_DOCUMENTS, selectedState],
    () => sharepointService.getRecentDocuments(selectedState as string),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
      enabled: !!selectedState,
    }
  );

  useEffect(() => {
    if (refinersQuery.data) {
      setRefiners(refinersQuery.data);
    }
  }, [refinersQuery.data, setRefiners]);

  const handleSelectSite = (site: ISite) => {
    setIsInitializing(true);
    setSelectedSite(site);
    setSelectedLocationId(site.LocationId);
    history.push('/documents');
  };

  const handleBackToStateSelection = () => {
    setSelectedState(null);
    setSelectedSite(null);
  };

  function previewFile(item: QuickReference | IFile) {
    if (item.fileType === '000') {
      return;
    }

    if (item.fileType !== 'pdf') {
      downloadFile(item);
      return;
    }

    // Always open preview in new tab for mobile resolution
    if (
      window.matchMedia(theme.breakpoints.down('sm').replace('@media ', ''))
        .matches
    ) {
      window.open(item.previewUrl);
      return;
    }

    setSelectedFile(item);
  }

  function downloadAction(url: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop() || '';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  function downloadFile(file: IFile | QuickReference) {
    // If fileType is 000, abort the download
    if (file.fileType === '000') {
      return;
    }

    downloadAction(file.previewUrl);
    setIsShowWaitMessage(true);
    return;
  }

  if (
    sitesQuery.isLoading ||
    externalLinksQuery.isLoading ||
    quickReferencesQuery.isLoading ||
    refinersQuery.isLoading ||
    recentDocumentsQuery.isLoading
  ) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const title = selectedState === 'NSW' ? 'New South Wales' : 'Queensland';
  const filteredSites = sites
    .filter((site) => site.State === selectedState)
    .sort((a, b) => {
      if (a.Title === 'Home') {
        return -1;
      }
      if (b.Title === 'Home') {
        return 1;
      }
      return a.Title.localeCompare(b.Title);
    });
  const locations = allLocations
    .filter((location) => location.state === selectedState)
    .sort((a, b) => a.title.localeCompare(b.title));
  const externalLinks = (externalLinksQuery.data ?? []).filter(
    (link) => link.state === selectedState
  );

  return (
    <>
      <Container maxWidth='xl' sx={{ marginTop: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <IconButton
            onClick={handleBackToStateSelection}
            sx={{ marginRight: 2, fontSize: '2rem', color: '#1A237E' }}
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant='h2'
            gutterBottom
            sx={{
              fontWeight: 'bold',
              color: '#1A237E',
              fontSize: '2.5rem',
              marginTop: 2,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Grid container spacing={4} justifyContent='center'>
          <Grid item xs={12} md={6} lg={4}>
            <Box
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                backgroundColor: '#FFFFFF',
              }}
            >
              <Box
                sx={{
                  padding: 3,
                  border: '4px solid #bfdbfe',
                  borderBottom: 'none',
                  borderTopLeftRadius: 12,
                  borderTopRightRadius: 12,
                  backgroundColor: '#e3f2fd',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 1,
                  }}
                >
                  <Inventory
                    sx={{ fontSize: 40, color: '#1A237E', marginRight: 2 }}
                  />
                  <Typography
                    variant='h5'
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: '#1A237E',
                      marginBottom: 0,
                    }}
                  >
                    {title} Sites
                  </Typography>
                </Box>
                <Typography variant='body2' color='textSecondary'>
                  Select a location to access the corresponding document
                  repository.
                </Typography>
              </Box>
              <Box
                sx={{
                  padding: 3,
                  borderBottomLeftRadius: 12,
                  borderBottomRightRadius: 12,
                  border: '4px solid #bfdbfe',
                  borderTop: 'none',
                }}
              >
                {locations.map((location) => (
                  <Accordion
                    key={location.id}
                    sx={{ marginBottom: 2, border: '2px solid #E0E0E0' }}
                    defaultExpanded={selectedState === 'QLD'}
                  >
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography sx={{ fontWeight: 'bold' }}>
                        {location.title}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {/* No items */}
                      {filteredSites.filter(
                        (site) => site.LocationId === location.id
                      ).length === 0 && (
                        <Typography color='textSecondary'>
                          No sites available for this location.
                        </Typography>
                      )}

                      {filteredSites
                        .filter((site) => site.LocationId === location.id)
                        .map((site) => (
                          <Box
                            key={site.Id}
                            onClick={() => handleSelectSite(site)}
                            sx={{
                              marginBottom: 2,
                              cursor: 'pointer',
                              borderRadius: 2,
                              boxShadow: 'none',
                              border: '2px solid #E0E0E0',
                              padding: 2,
                              backgroundColor: '#FFFFFF',
                              transition: 'border-color 0.3s',
                              '&:active': {
                                borderColor: '#1A237E',
                                backgroundColor: '#e3f2fd',
                                border: '2px solid #bfdbfe',
                              },
                              '&:hover': {
                                borderColor: '#1A237E',
                                backgroundColor: '#e3f2fd',
                                border: '2px solid #bfdbfe',
                              },
                            }}
                          >
                            <Typography sx={{ fontWeight: 'bold' }}>
                              {site.Title}
                            </Typography>
                          </Box>
                        ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} lg={8}>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                    backgroundColor: '#f6f9fc',
                  }}
                >
                  <CardContent sx={{ padding: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 1,
                      }}
                    >
                      <LinkIcon
                        sx={{
                          fontSize: 40,
                          color: '#1A237E',
                          marginRight: 2,
                        }}
                      />
                      <Typography
                        variant='h5'
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: '#1A237E',
                          marginBottom: 0,
                        }}
                      >
                        External Links
                      </Typography>
                    </Box>
                    <Typography variant='body2' color='textSecondary'>
                      Navigate to related external resources.
                    </Typography>
                    <Box sx={{ marginTop: 2 }}>
                      {externalLinks.map((link) => (
                        <Link
                          key={link.order}
                          href={link.url}
                          target={link.target}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '6px',
                            textDecoration: 'none',
                            borderRadius: 2,
                            color: '#000000',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: '#e3f2fd',
                              color: '#1A237E',
                              cursor: 'pointer',
                            },
                          }}
                        >
                          <Typography>{link.title}</Typography>
                          <Launch />
                        </Link>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Card
                  sx={{
                    borderRadius: 4,
                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                    backgroundColor: '#f6f9fc',
                  }}
                >
                  <CardContent sx={{ padding: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: 1,
                      }}
                    >
                      <Description
                        sx={{
                          fontSize: 40,
                          color: '#1A237E',
                          marginRight: 2,
                        }}
                      />
                      <Typography
                        variant='h5'
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: '#1A237E',
                          marginBottom: 0,
                        }}
                      >
                        Quick Reference Guides
                      </Typography>
                    </Box>
                    <Typography variant='body2' color='textSecondary'>
                      Documentation and support resources.
                    </Typography>
                    <Box sx={{ marginTop: 2 }}>
                      {quickReferencesQuery.data?.map((guide) => (
                        <Box
                          key={guide.order}
                          onDoubleClick={() => previewFile(guide)}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            textDecoration: 'none',
                            borderRadius: 2,
                            color: '#000000',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: '#e3f2fd',
                              color: '#1A237E',
                              cursor: 'pointer',
                            },
                          }}
                        >
                          <Typography onClick={() => previewFile(guide)}>
                            {guide.title}
                          </Typography>
                          <Box
                            sx={{
                              fontSize: '1.6rem',
                              display: 'flex',
                              flexDirection: 'row',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            {guide.fileType === 'pdf' && (
                              <IconButton onClick={() => previewFile(guide)}>
                                <Icon icon='ant-design:file-pdf-filled' fr='' />
                              </IconButton>
                            )}
                            <IconButton onClick={() => downloadFile(guide)}>
                              <DownloadIcon />
                            </IconButton>
                          </Box>{' '}
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            <Box marginTop={4}>
              <RecentDocumentsCard
                recentDocuments={recentDocumentsQuery.data ?? []}
                previewFile={previewFile}
                downloadFile={downloadFile}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {selectedFile && (
        <FilePreview
          title={selectedFile.title}
          show={true}
          fileSource={selectedFile.previewUrl}
          onClose={() => setSelectedFile(null)}
        ></FilePreview>
      )}

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={isShowWaitMessage}
        autoHideDuration={3000}
        onClose={() => setIsShowWaitMessage(false)}
        message={'File has been requested. Please wait for a few seconds.'}
      />
    </>
  );
}
